import { useState } from 'react';
import { CoverageKey, IInsightsFilter, isFiltered } from './agencyDigest';

export interface IInsightsNarrowing {
    filter: IInsightsFilter;
    narrowed: boolean;
    /** Which coverage meter is open. A disclosure, not part of the narrowing. */
    openMeter?: CoverageKey;
    toggleKind: (kind: IInsightsFilter['kind']) => void;
    toggleStanding: (standing: IInsightsFilter['standing']) => void;
    toggleMeter: (key: CoverageKey) => void;
    /** Open or close a named meter outright, for a control whose label promises one direction. */
    setMeter: (key?: CoverageKey) => void;
    clear: () => void;
}

/**
 * What the reader has narrowed the tab to, and the controls that set it.
 *
 * Every toggle clears on a second press, so each control is its own way back out and the reader
 * never has to hunt for Clear to undo the click they just made.
 *
 * The open coverage meter rides along here because it is set from the tile row as well as from the
 * meters, but it is deliberately not part of the filter: coverage is a property of the client rather
 * than of a finding, so it does not narrow the diagrams.
 */
export const useInsightsFilter = (): IInsightsNarrowing => {
    const [filter, setFilter] = useState<IInsightsFilter>({});
    const [openMeter, setOpenMeter] = useState<CoverageKey | undefined>();

    return {
        filter,
        narrowed: isFiltered(filter),
        openMeter,
        toggleKind: kind => setFilter(current => ({ ...current, kind: current.kind === kind ? undefined : kind })),
        toggleStanding: standing => setFilter(current => ({
            ...current,
            standing: current.standing === standing ? undefined : standing
        })),
        toggleMeter: key => setOpenMeter(current => (current === key ? undefined : key)),
        setMeter: key => setOpenMeter(key),
        clear: () => setFilter({})
    };
};
